import React, { useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import OverviewCardShell from "./cards/OverviewCardShell";
import DonutProgress from "./charts/DonutProgress";
import RevenueAreaTrend from "./charts/RevenueAreaTrend";
import StudentBars from "./charts/StudentBars";

import {
  Calender,
  IncrementArrow,
  InstructorCardIcon,
  SimpleUserIcon,
  UserIcon,
} from "../AdminIcons";
import { dashboardOverviewData, calculateInstructorMetrics } from "../constants";
import { useGetVerifiedAndUnverifiedInstructorsQuery } from "../../services/instructor/instructorApi";

const DashboardOverviewSection = () => {
  const navigate = useNavigate();
  const { data: instructorData } = useGetVerifiedAndUnverifiedInstructorsQuery();
  const todayLabel = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const {
  
    revenue,
    studentMetrics,
  } = dashboardOverviewData;

  const { verifiedInstructors, pendingVerification } = useMemo(() => {
    if (!instructorData?.data) {
      return calculateInstructorMetrics(0, 0);
    }

    return calculateInstructorMetrics(
      instructorData.data.verifiedInstructors,
      instructorData.data.unverifiedInstructors,
    );
  }, [instructorData]);

  return (
    <section className="pt-6 sm:pt-8 md:pt-12 px-5 md:pl-3 md:pr-[34px]">
      <div className=" mb-7 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl  font-bold   text-[#111827]">
          Dashboard Overview
        </h1>

        <p className="flex  items-center gap-2 text-sm font-normal text-[#6B7280]">
          <span>{todayLabel}</span>
          <Calender />
        </p>
      </div>

      <div className="grid w-full grid-cols-1 md:grid-cols-2 gap-4 xl:grid-cols-3 max-[1688px]:grid-cols-[1.1fr_1fr_1fr_1.55fr]">
        <OverviewCardShell
          title="Total Revenue"
          action={
            <button
              type="button"
              className="flex  items-center gap-1.5 text-xs sm:text-sm  text-[#6B7280]"
            >
              {revenue.period}
              <ChevronDown size={16} />
            </button>
          }
        >
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xl sm:text-2xl font-bold  leading-8 text-[#111827] sm:text-[30px] sm:leading-9">
              {revenue.amount}
            </p>
            <span className="flex gap-1 items-baseline  text-xs sm:text-sm  font-medium  text-[#22C55E]">
              <IncrementArrow />

              {revenue.trend}
            </span>
          </div>

          <p className="mt-1 text-xs sm:text-sm  text-[#6B7280]">
            {revenue.comparisonLabel}
          </p>
          <RevenueAreaTrend />
        </OverviewCardShell>

        <OverviewCardShell
          title="Verified Instructors"
          icon={<InstructorCardIcon />}
        >
          <div className="flex flex-col items-center pt-5 text-center">
            <DonutProgress
              value={verifiedInstructors.completion}
              color={verifiedInstructors.color}
              label={`${verifiedInstructors.completion}%`}
            />
            <p className="mt-7 text-xl sm:text-2xl font-bold   text-[#111827]">
              {verifiedInstructors.count}
            </p>
            <p className="text-xs sm:text-sm font-normal text-[#6B7280]">
              {verifiedInstructors.label}
            </p>
          </div>
        </OverviewCardShell>

        <OverviewCardShell title="Pending Verification" icon={<UserIcon />}>
          <div className="flex flex-col items-center pt-4 text-center">
            <DonutProgress
              value={pendingVerification.completion}
              color={pendingVerification.color}
              label={`${pendingVerification.completion}%`}
            />
            <p className="mt-[22px] text-2xl font-bold   text-[#111827]">
              {pendingVerification.count}
            </p>
            <button
              type="button"
              onClick={() => navigate("/admin/manage-user")}
              className="mt-2 rounded bg-[#923D8C] px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm   text-white transition hover:bg-[#7E3578]"
            >
              {pendingVerification.cta}
            </button>
          </div>
        </OverviewCardShell>

        <OverviewCardShell className="xl:col-span-2" title="Student Metrics" icon={<SimpleUserIcon />}>
          <div className="grid grid-cols-2 gap-y-4">
            <div>
              <p className="text-xs sm:text-sm   text-[#6B7280]">
                Active Students
              </p>
              <p className="text-xl sm:text-2xl font-bold   text-[#111827]">
                {studentMetrics.activeStudents}
              </p>
            </div>

            <div>
              <p className="text-xs sm:text-sm   text-[#6B7280]">
                New Registrations
              </p>
              <p className=" text-xl sm:text-2xl font-bold   text-[#111827]">
                {studentMetrics.newRegistrations}
              </p>
              <p className=" flex gap-2 sm:gap-4 text-xs  text-[#22C55E]">
                <IncrementArrow /> {studentMetrics.weeklyTrend}
              </p>
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-1 flex items-center justify-between">
              <p className="text-xs sm:text-sm   text-[#6B7280]">
                Engagement Rate
              </p>
              <p className="text-xs sm:text-sm  font-medium text-[#111827]">
                {studentMetrics.engagementRate}%
              </p>
            </div>

            <div className="h-1.5 sm:h-2 w-full rounded-full bg-[#E5E7EB]">
              <div
                className="h-full rounded-full bg-[#923D8C]"
                style={{ width: `${studentMetrics.engagementRate}%` }}
              />
            </div>

            <StudentBars values={studentMetrics.bars} />
          </div>
        </OverviewCardShell>
      </div>
    </section>
  );
};

export default DashboardOverviewSection;
